import { useDispatch, useSelector } from 'react-redux';

import {
    Alert,
    ArrowDropDownIcon,
    Box,
    Button,
    CalendarMonthIcon,
    CropFreeIcon,
    GpsFixedIcon,
    Histogram,
    LocationOnIcon,
    Theme,
    Typography,
    useTheme
} from '@methanesat/ui-components';

import { BarGradientProps, buildLinearGradient, buildNonLinearGradient } from '@methanesat/ui-components';
import type { HistogramRow } from '@methanesat/ui-components/src/components/Histogram/Histogram';
import { useMemo } from 'react';
import {
    FAQ_DISTINCT_SOURCES,
    getColorScaleDefinition,
    getDispersedColorAnchors,
    METHANESAT_FAQ_URL,
    MIN_SIGNIFICANT_RASTER_EMISSION,
    PLATFORM_RASTER_SIZE,
    PLUME_EMISSION_DETECTION_THRESHOLD,
    ScaleDataKeys,
    getFilledTargetColor,
    TARGET_GROUP_LOOKUP_BY_CHILD,
    FAQ_ALGORITHM_VERSION
} from '../../../../consts';
import { SHOW_OGI_NEAR_RASTER_COORDINATES, SHOW_RASTER_CI } from '../../../../environmentVariables';
import {
    useAreaEmissionsUnitsLabel,
    useLengthUnitsLabel,
    usePlumeEmissionsUnitsLabel,
    useTranslate
} from '../../../../hooks';
import {
    resetOgiNearby,
    selectEmissionsMapFilters,
    selectL4MSATColorScale,
    selectMethaneProduct,
    selectPlatform,
    useGetHistogramDataQuery
} from '../../../../reducers';
import type { RootState } from '../../../../store';
import {
    AreaEmissionDrawerProps,
    AreaEmissionsProducts,
    CardUrlState,
    DispersedAccordionContent,
    DispersedSourceAccordionProps,
    DistinctPointAccordionContent,
    DistinctPointSourceAccordionProps,
    EmissionDrawerCardData,
    HistogramData,
    isAreaEmissionInfo,
    isPlumeEmissionInfo,
    isPlumeProperties,
    isRasterInfo,
    isTotalOnlyInfo,
    MethaneLayerIds,
    PickInfoToPrioritize,
    Platforms,
    PlumeDrawerProps,
    SourceIds,
    STACCollection,
    TotalOnlyAccordionContent,
    TotalOnlyAccordionProps,
    TotalOnlyInfo
} from '../../../../types';
import {
    ErrorBoundary,
    formatCoordinates,
    formatEmissions,
    formatMeasurementTime,
    getColorRangeColors,
    getDispersedColorFromMethaneValue,
    getLinearPositionBetweenAnchorsOnScale,
    getLinearPositionOnScale,
    getLogPositionOnScale,
    getRasterColorMapName,
    getStacCaptureEmissions,
    getStacPlumeTimes,
    handleErrorBoundaryError
} from '../../../../utils';
import { OGINearEmissions } from '../DataSpecific';
import { EmissionsHeadline } from '../DataSpecific/EmissionsDrawers/EmissionsHeadline';
import { MoreInfoSection } from '../DataSpecific/EmissionsDrawers/MoreInfoSection';

/**
 * Function to handle getting the parent target id from PickInfo. Depending on the
 * info type, the property may be different or differently nested.
 */
function getParentTargetId(info: PickInfoToPrioritize): string | null | undefined {
    // rasters have this added to the object in
    // getTitilerPgStacLayer (maps/src/layers/getTitilerAreaEmissionLayer.ts)
    if ('parentTargetId' in info.object) return info.object.parentTargetId;

    // points have this added to the layer in
    // getPointSourceItem (hooks/data/stacData/stac.tsx)
    if ('parentTargetId' in info.layer) return info.layer.parentTargetId;

    // targets have this in their properties by default
    if (info.object.properties && 'target_id' in info.object.properties) return info.object.properties?.target_id;

    // fallback
    return null;
}

/**
 * Function to build the detail table rows for raster/dispersed emissions
 */
function useMethaneRasterDetailRows(info: AreaEmissionDrawerProps['info'] | TotalOnlyInfo) {
    const t = useTranslate();
    const lengthUnitsLabel = useLengthUnitsLabel();
    const platform = useSelector(selectPlatform);
    const product = useSelector(selectMethaneProduct);

    if (isTotalOnlyInfo(info)) {
        return [
            {
                label: t('emissionsMapPage.mapModals.emissions.measurementTimeLabel'),
                value: formatMeasurementTime(
                    info.object.properties.start_datetime,
                    info.object.properties.end_datetime
                ),
                mobileLabel: t('emissionsMapPage.mapModals.emissions.measurementTimeMobileLabel'),
                icon: <CalendarMonthIcon />
            },
            {
                label: t('emissionsMapPage.mapModals.common.algorithmVersionLabel'),
                href: `${METHANESAT_FAQ_URL}${FAQ_ALGORITHM_VERSION}`,
                value: info.object.properties.processor_version
            }
        ];
    }
    const pixelSize =
        info.object.properties.pixelSizeKm2 && product === AreaEmissionsProducts.l3
            ? info.object.properties.pixelSizeKm2 * 1e6
            : info.object.properties.pixelSizeKm2;
    const platformRasterSize = pixelSize ? Math.round(Math.sqrt(pixelSize)) : PLATFORM_RASTER_SIZE[platform][product];

    return [
        ...(info.coordinate
            ? [
                  {
                      label: t('emissionsMapPage.mapModals.common.dataTable.coordinatesLabel'),
                      value: formatCoordinates(info.coordinate),
                      icon: <GpsFixedIcon />
                  }
              ]
            : []),
        {
            label: t('emissionsMapPage.mapModals.emissions.measurementTimeLabel'),
            value: formatMeasurementTime(
                info.object.properties.collectionStartTime,
                info.object.properties.collectionEndTime
            ),
            mobileLabel: t('emissionsMapPage.mapModals.emissions.measurementTimeMobileLabel'),
            icon: <CalendarMonthIcon />
        },
        {
            label: t('emissionsMapPage.mapModals.common.dimensionsLabel'),
            value: `~ ${platformRasterSize} ${lengthUnitsLabel} x ${platformRasterSize} ${lengthUnitsLabel}`,
            mobileLabel: t('emissionsMapPage.mapModals.common.dimensionsMobileLabel'),
            icon: <CropFreeIcon />
        },
        {
            label: t('emissionsMapPage.mapModals.common.algorithmVersionLabel'),
            href: `${METHANESAT_FAQ_URL}${FAQ_ALGORITHM_VERSION}`,
            value: info.object.properties.processorVersion
        }
    ];
}

/**
 * Function to build the detail table rows for point/distinct emissions
 */
function useGetPlumeDetailRows(info: PlumeDrawerProps['info']) {
    const t = useTranslate();
    const timeLabel = t('emissionsMapPage.mapModals.emissions.measurementTimeLabel');
    const timeMobileLabel = t('emissionsMapPage.mapModals.emissions.measurementTimeMobileLabel');
    const coordinatesLabel = t('emissionsMapPage.mapModals.common.dataTable.coordinatesLabel');
    const locationLabel = t('emissionsMapPage.mapModals.common.dataTable.locationLabel');

    const { startTime, endTime } = getStacPlumeTimes(info.object.properties);
    return [
        {
            label: locationLabel,
            value: info.object.properties.location,
            icon: <LocationOnIcon />
        },
        ...(info.object.geometry.coordinates
            ? [
                  {
                      label: coordinatesLabel,
                      value: formatCoordinates(info.object.geometry.coordinates),
                      icon: <GpsFixedIcon />
                  }
              ]
            : []),
        {
            label: timeLabel,
            value: formatMeasurementTime(startTime, endTime),
            mobileLabel: timeMobileLabel,
            icon: <CalendarMonthIcon />
        }
    ];
}

import { colorArrayToRgb } from '@methanesat/colors';

/**
 * Converts an array of emission - distinct or dispersed - pick infos, sorted by priority, into an array of `DrawerCardContentProps`,
 * grouping by target ID to generate the data needed for each drawer card. Preserves priority order
 * the array card data returned.
 */
export const emissionsInfoToCardProps = (infos: PickInfoToPrioritize[]): EmissionDrawerCardData[] => {
    // Filter for the info types that we can create accordions for. In the future, targets and OGI should be added here
    const infosToDisplay = infos.filter((i) => isRasterInfo(i) || isPlumeEmissionInfo(i) || isTotalOnlyInfo(i));
    const seen = new Set();
    const cards: EmissionDrawerCardData[] = [];
    const selectedCoordinates =
        infos.find((info) => (isRasterInfo(info) || isTotalOnlyInfo(info)) && 'coordinate' in info)?.coordinate ??
        infos.find((info) => 'coordinate' in info)?.coordinate ??
        undefined;

    infosToDisplay.forEach((info) => {
        const targetId = getParentTargetId(info);
        if (!targetId) return;

        const targetGroupId = TARGET_GROUP_LOOKUP_BY_CHILD[targetId];
        // for each new targetId, extract all the infos for that target
        if (!seen.has(targetGroupId || targetId)) {
            const infosForCard = infos.filter((i) => {
                const infoTargetId = getParentTargetId(i);
                const infoTargetGroupId = infoTargetId && (TARGET_GROUP_LOOKUP_BY_CHILD[infoTargetId] || infoTargetId);
                return infoTargetGroupId === (targetGroupId || targetId);
            });

            // Resolve an `itemId` across mixed pick-info types so the active capture can be restored
            // even when some variants are missing from the selection.
            const itemId =
                infosForCard.find((info) => isRasterInfo(info) || isTotalOnlyInfo(info))?.object.id ||
                infosForCard.find(isPlumeEmissionInfo)?.object.relatedRasterItemId ||
                infosForCard.find(isPlumeEmissionInfo)?.object.itemId;

            cards.push({
                targetGroupId,
                targetId,
                itemId,
                startDateTime: isPlumeProperties(infosForCard[0].object.properties)
                    ? getStacPlumeTimes(infosForCard[0].object.properties).startTime
                    : infosForCard[0].object.properties?.collectionStartTime ||
                      infosForCard[0].object.properties?.start_datetime,
                selectedCoordinates,
                distinctPointEmissions: infosForCard.filter(isPlumeEmissionInfo),
                dispersedSourceEmissions: infosForCard.filter((i) => isRasterInfo(i) || isTotalOnlyInfo(i)),
                dataUnavailable: false
            });
            seen.add(targetId);
        }
    });

    return cards;
};

/**
 * Rebuilds the card list in requested target order and inserts unavailable cards for targets
 * with no refreshed data.
 */
export const fillUnavailableEmissionCards = ({
    cards,
    targetIds,
    targetDates,
    emissionsDrawerSelection,
    fallbackDate
}: {
    cards: EmissionDrawerCardData[];
    targetIds: string[];
    targetDates: Record<string, string | undefined>;
    emissionsDrawerSelection?: CardUrlState | null;
    fallbackDate: string;
}) => {
    const cardsByTargetOrGroupId = new Map(
        cards.map((card) => {
            const { targetGroupId, targetId } = card;
            return [targetGroupId || targetId, card];
        })
    );
    const lng = emissionsDrawerSelection?.['selected-lng'];
    const lat = emissionsDrawerSelection?.['selected-lat'];
    const selectedCoordinates = typeof lng === 'number' && typeof lat === 'number' ? [lng, lat] : undefined;

    return targetIds.map((targetId) => {
        const targetGroupId = TARGET_GROUP_LOOKUP_BY_CHILD[targetId];
        const existingCard = cardsByTargetOrGroupId.get(targetGroupId || targetId);

        if (existingCard) return existingCard;

        const itemId = emissionsDrawerSelection?.[`target-${targetId}-itemId`];

        return {
            ...(targetGroupId && { targetGroupId }),
            targetId,
            itemId: typeof itemId === 'string' ? itemId : undefined,
            startDateTime: targetDates[targetGroupId || targetId] || fallbackDate,
            selectedCoordinates,
            distinctPointEmissions: [],
            dispersedSourceEmissions: [],
            dataUnavailable: true
        };
    });
};

/** Creates a unique ID for accordions */
export const getUniqueAccordionId = (info: PickInfoToPrioritize | TotalOnlyInfo) => {
    // area emissions accordions only need the layer id because there can be only
    // 1 raster accordion per target. Since raster layers are named with the target id as well,
    // this is sufficient
    if (isAreaEmissionInfo(info)) return `${info.layer.id}`;
    // plume emissions accordions need layer id + plume id since it is possible for multiple
    // plumes to have accordions in a given target
    if (isPlumeEmissionInfo(info)) return `${info.layer.id}_${info.object.properties.plume_id}`;
    // targets need layer id + index - the target layer is generic, so does not include the target id
    return `${info.layer.id}_${info.index}`;
};

/** Creates the headline text for dispersed emission accordions */
export const useGetEmissionsText = ({
    platform,
    methaneValue,
    pixelDiscarded,
    totalOnly
}: {
    platform: Platforms;
    methaneValue?: number;
    pixelDiscarded?: boolean;
    totalOnly?: boolean;
}) => {
    const t = useTranslate();
    if (totalOnly) return t('emissionsMapPage.mapModals.dispersed.totalOnlyHeadline');
    if (pixelDiscarded) return t('emissionsMapPage.mapModals.dispersed.discardedByTwoStepValue');
    if (methaneValue && methaneValue < MIN_SIGNIFICANT_RASTER_EMISSION[platform])
        return t(`emissionsMapPage.mapModals.dispersed.minSignificantMessage.${platform}`, {
            minimum: MIN_SIGNIFICANT_RASTER_EMISSION[platform]
        });
    return '';
};

/** Creates props for point source accordions from a pick info object */
export const useGetPointSourceAccordionProps = (
    info: DistinctPointSourceAccordionProps['info'],
    ogiError: boolean
): DistinctPointAccordionContent => {
    const t = useTranslate();
    const dispatch = useDispatch();
    const platform = useSelector(selectPlatform);

    const id = getUniqueAccordionId(info);
    const title = t('emissionsMapPage.mapModals.emissions.plumeTitle');
    const { flux: plumeFlux, flux_lo: plumeFluxLow, flux_hi: plumeFluxHigh } = info.object.properties;
    const units = usePlumeEmissionsUnitsLabel();

    // Emissions headline
    const emissionsHeadline = (
        <EmissionsHeadline
            emissions={plumeFlux}
            lowEmissions={plumeFluxLow}
            highEmissions={plumeFluxHigh}
            units={units}
            ciVariant="percent"
            typographyVariant="h6"
            data-testid={`point-source-accordion-headline-${id}`}
        />
    );

    // Data rows
    const dataRows = useGetPlumeDetailRows(info);

    const [longitude, latitude] = info.object.geometry.coordinates;

    const showOgiDetails = useSelector((state: RootState) => selectEmissionsMapFilters(state).ogimFeatures.length > 0);

    //first paragraph of content is hidden when ogi details are visible, only uncertainty assessment note shows
    const detailsText = `${
        showOgiDetails
            ? ''
            : t('emissionsMapPage.mapModals.plumes.moreInfo.content', {
                  platform: t(`emissionsMapPage.mapModals.common.instrument.${platform}`),
                  plumeEmissionDetectionThreshold: PLUME_EMISSION_DETECTION_THRESHOLD[platform]
              })
    }${t('emissionsMapPage.mapModals.plumes.moreInfo.uncertaintyAssessment', {
        platform: t(`emissionsMapPage.mapModals.common.instrument.${platform}`),
        link: `${METHANESAT_FAQ_URL}${FAQ_DISTINCT_SOURCES}`
    })}`;

    const detailsContent = <MoreInfoSection content={detailsText} />;

    const ogiContent = (
        <>
            {!ogiError && (
                <ErrorBoundary
                    key={info.object.id}
                    onError={handleErrorBoundaryError(`PlumeDrawerOGINearEmissions: ${info.object.id}`)}
                    fallbackRender={({ resetErrorBoundary }) => {
                        return (
                            <Alert
                                action={
                                    <Button
                                        onClick={resetErrorBoundary}
                                        data-testid="retry-ogc-fetch-button"
                                        size="small"
                                    >
                                        {t('emissionsMapPage.error.retry')}
                                    </Button>
                                }
                                severity="error"
                            >
                                {t('emissionsMapPage.error.general')}
                            </Alert>
                        );
                    }}
                >
                    <OGINearEmissions
                        info={info}
                        emissionRate={plumeFlux}
                        longitude={longitude}
                        latitude={latitude}
                        sourceType={SourceIds.distinct}
                        showDetails={showOgiDetails}
                        resetMap={() => dispatch(resetOgiNearby())}
                    />
                </ErrorBoundary>
            )}
        </>
    );

    return {
        id,
        title,
        emissionsHeadline,
        dataRows,
        platform,
        showOgiDetails,
        ogiContent,
        detailsContent
    };
};

/** Creates an array of data from an API call that the histogram can use */
function getHistogramFromDataResponse(colors: string[], response: HistogramData, platform: Platforms) {
    let histogramArray = [];

    const countArray = response?.COG.b1.histogram[0];
    const rangeArray = response?.COG.b1.histogram[1];
    let excludedCount = 0;

    if (countArray && rangeArray) {
        const normalizedRangeArray = platform === 'MethaneAIR' ? rangeArray : rangeArray.map((x) => x / 16);

        for (let i = 0; i < countArray.length; i++) {
            const newHistogramItem = {
                label: i === countArray.length - 1 ? `${normalizedRangeArray[i]} + ` : `${normalizedRangeArray[i]}`,
                range: `${normalizedRangeArray[i]} - ${normalizedRangeArray[i + 1]}`,
                value: countArray[i],
                color: colors[i]
            };
            histogramArray.push(newHistogramItem);
        }
    }
    // For MSAT, exclude values below the noise threshold (the 2 lowest bins)
    if (platform === Platforms.MSAT) {
        excludedCount = histogramArray[0]?.value + histogramArray[1]?.value;
        histogramArray = histogramArray.slice(2);
    }
    return { bins: histogramArray, totalCount: response?.COG.b1.count, excludedCount };
}

/** gets histogram data and formats it into what the component expects */
function useHistogramData(
    collection: STACCollection,
    info: DispersedSourceAccordionProps['info'],
    platform: Platforms,
    product: AreaEmissionsProducts,
    theme: Theme,
    skip?: boolean
) {
    const colors = getColorRangeColors(theme, true, product, platform);

    const selectedStacItemId = info.object.id;

    const bins =
        platform === Platforms.MSAT
            ? '0,4,8,16,24,36,52,72,96,124,156,192,232,272,1000000'
            : '0,1,2,3,4,5,6,7,8,9,10,11,12,13';

    const { data: histogramData, isFetching: fetchingData } = useGetHistogramDataQuery(
        {
            collection,
            selectedStacItemId,
            bins
        },
        { skip }
    );

    const data = useMemo(() => {
        if (fetchingData) return null;

        return histogramData;
    }, [histogramData, fetchingData]);

    return getHistogramFromDataResponse(colors, data as HistogramData, platform);
}

/** Creates props for dispersed source accordions from a pick info object */
export const useGetDispersedSourceAccordionProps = (
    info: DispersedSourceAccordionProps['info'],
    ogiError: boolean
): DispersedAccordionContent => {
    const t = useTranslate();
    const dispatch = useDispatch();
    const theme = useTheme();

    const product = useSelector(selectMethaneProduct);
    const platform = useSelector(selectPlatform);
    const units = useAreaEmissionsUnitsLabel(product);

    const { pixelSizeKm2, methane, pixelDiscarded } = info.object.properties;

    const id = getUniqueAccordionId(info);
    const title = t(`emissionsMapPage.mapModals.emissions.areaTitle.${product}`) || '';

    // pixelSize should be in units of meters if showing concentrations
    const pixelSize = pixelSizeKm2 && product === AreaEmissionsProducts.l3 ? pixelSizeKm2 * 1e6 : pixelSizeKm2;
    const platformRasterSize = pixelSize ? Math.round(Math.sqrt(pixelSize)) : PLATFORM_RASTER_SIZE[platform][product];

    const methaneValue = product === AreaEmissionsProducts.l3 ? methane : methane / Math.pow(platformRasterSize, 2);

    // Emissions headline
    const methaneLowerBound = info.object.properties.methaneLowerBound
        ? product === AreaEmissionsProducts.l3
            ? info.object.properties.methaneLowerBound
            : info.object.properties.methaneLowerBound / Math.pow(platformRasterSize, 2)
        : null;

    const methaneUpperBound = info.object.properties.methaneUpperBound
        ? product === AreaEmissionsProducts.l3
            ? info.object.properties.methaneUpperBound
            : info.object.properties.methaneUpperBound / Math.pow(platformRasterSize, 2)
        : null;

    const emissionsText = useGetEmissionsText({ platform, methaneValue, pixelDiscarded });

    const emissionsHeadline = (
        <EmissionsHeadline
            emissions={methaneValue}
            emissionsText={emissionsText}
            {...(SHOW_RASTER_CI && {
                highEmissions: methaneUpperBound,
                lowEmissions: methaneLowerBound
            })}
            units={units}
            typographyVariant="h6"
            data-testid={`dispersed-accordion-headline-${id}`}
        />
    );

    // Data rows
    const dataRows = useMethaneRasterDetailRows(info);

    const [longitude, latitude] = info.coordinate ?? [0, 0];

    const showOgiDetails = useSelector((state: RootState) => selectEmissionsMapFilters(state).ogimFeatures.length > 0);

    // get histogram data and put into array
    const histogramData = useHistogramData(info.object.collection, info, platform, product, theme);

    // Percentage of data below the noise threshold.
    const belowNoisePercentage = ((histogramData.excludedCount / histogramData.totalCount) * 100).toFixed(2);

    // Displays the percentage of pixels that a bin contains
    const tooltipLabelHandler = (value: number | null, _context: { dataIndex: number }, _data: HistogramRow[]) => {
        const percent = value ? value.toFixed(2) : 0;
        return t('emissionsMapPage.mapModals.dispersed.histogram.tooltipLabelText', { percent: percent });
    };

    // Displays the bin range instead of the shortened label in the tooltip
    const tooltipBinFormatter = (value: string, location: string) => {
        if (location === 'tick') return value;
        const range = histogramData.bins.find((d) => d.label === value)?.range;
        return range ? `${range} ${t('emissionsMapPage.units.kgHrKm2')}` : '';
    };

    const legend = (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ArrowDropDownIcon sx={{ fontSize: 22 }} />
            <Typography variant="caption">
                {t('emissionsMapPage.mapModals.dispersed.histogram.selectionLegendLabel')}
            </Typography>
        </Box>
    );

    const caption = (
        <Typography variant="caption">
            {t('emissionsMapPage.mapModals.dispersed.histogram.noiseThresholdDisclaimer', {
                percent: belowNoisePercentage
            })}
        </Typography>
    );

    const additionalContent =
        product === AreaEmissionsProducts.l3 ? null : (
            <>
                {SHOW_OGI_NEAR_RASTER_COORDINATES && !ogiError && (
                    <ErrorBoundary
                        key={info.object.id}
                        onError={handleErrorBoundaryError(`PlumeDrawerOGINearEmissions: ${info.object.id}`)}
                        fallbackRender={({ resetErrorBoundary }) => {
                            return (
                                <Alert
                                    action={
                                        <Button
                                            onClick={resetErrorBoundary}
                                            data-testid="retry-ogc-fetch-button"
                                            size="small"
                                        >
                                            {t('emissionsMapPage.error.retry')}
                                        </Button>
                                    }
                                    severity="error"
                                >
                                    {t('emissionsMapPage.error.general')}
                                </Alert>
                            );
                        }}
                    >
                        <OGINearEmissions
                            info={info}
                            emissionRate={methaneValue}
                            longitude={longitude}
                            latitude={latitude}
                            sourceType={SourceIds.dispersed}
                            showDetails={showOgiDetails}
                            resetMap={() => dispatch(resetOgiNearby())}
                        />
                    </ErrorBoundary>
                )}
            </>
        );

    const ogiNearbyContent = (
        <ErrorBoundary
            key={info.object.id}
            onError={handleErrorBoundaryError(`PlumeDrawerOGINearEmissions: ${info.object.id}`)}
            fallbackRender={({ resetErrorBoundary }) => {
                return (
                    <Alert
                        action={
                            <Button onClick={resetErrorBoundary} data-testid="retry-ogc-fetch-button" size="small">
                                {t('emissionsMapPage.error.retry')}
                            </Button>
                        }
                        severity="error"
                    >
                        {t('emissionsMapPage.error.general')}
                    </Alert>
                );
            }}
        >
            <OGINearEmissions
                info={info}
                emissionRate={methaneValue}
                longitude={longitude}
                latitude={latitude}
                sourceType={SourceIds.dispersed}
                showDetails={showOgiDetails}
                resetMap={() => dispatch(resetOgiNearby())}
            />
        </ErrorBoundary>
    );

    const histogramContent = (
        <Histogram
            data={histogramData}
            selectedValue={methaneValue}
            chartColor="white"
            sx={{ height: 325, width: { sm: 375, md: 325, lg: 375 }, marginLeft: -6 }}
            legend={legend}
            yAxisWidth={50}
            xAxisLabel={t('emissionsMapPage.mapModals.dispersed.histogram.xAxisLabel')}
            yAxisLabel={t('emissionsMapPage.units.kgHrKm2')}
            tooltipLabelHandler={tooltipLabelHandler}
            tooltipBinFormatter={tooltipBinFormatter}
            {...(histogramData.excludedCount > 0 && { caption })}
        />
    );

    const l4MSATColorScale = useSelector(selectL4MSATColorScale);
    const colorScaleName = getRasterColorMapName(platform, product, l4MSATColorScale);

    // Bar chart props
    const {
        MIN: areaEmissionsMin,
        MAX: areaEmissionsMax,
        METHANE_BINS: areaEmissionMethaneBins,
        ANCHOR_PERCENTAGES: areaEmissionsPercentages,
        COLOR_BIN_THRESHOLDS: colorScaleBins,
        LABELS: positionedLabels
    } = getColorScaleDefinition(platform, product, MethaneLayerIds.areaEmissionRaster, colorScaleName);
    const [barChartMin, barChartMax] = [areaEmissionsMin, areaEmissionsMax];

    const barChartValue = methaneValue;
    const barGradientColors = getColorRangeColors(theme, true, product, platform, colorScaleName as ScaleDataKeys);
    const barGradientPercentages = areaEmissionsPercentages;
    const anchors = getDispersedColorAnchors(platform, product, barGradientColors);

    const iconColor = getDispersedColorFromMethaneValue(
        methaneValue,
        barChartMin,
        barChartMax,
        barGradientColors,
        colorScaleBins as number[] | undefined,
        anchors
    );

    // Bar gradient text labels
    const barChartMinLabel =
        product === AreaEmissionsProducts.l4
            ? t('emissionsMapPage.drawerWithCards.barGradientMinLabel', {
                  value: MIN_SIGNIFICANT_RASTER_EMISSION[platform]
              })
            : t('emissionsMapPage.drawerWithCards.barGradientMinLabel', {
                  value: formatEmissions(barChartMin, 'ppb')
              });
    const barChartMaxLabel =
        product === AreaEmissionsProducts.l4
            ? t('emissionsMapPage.drawerWithCards.barGradientMaxLabel', {
                  value: formatEmissions(barChartMax)
              })
            : t('emissionsMapPage.drawerWithCards.barGradientMaxLabel', {
                  value: formatEmissions(barChartMax, 'ppb')
              });

    const barGradientValuePosition =
        product === AreaEmissionsProducts.l3
            ? getLogPositionOnScale({ min: barChartMin, max: barChartMax, value: barChartValue })
            : positionedLabels && areaEmissionMethaneBins && barGradientPercentages
              ? getLinearPositionBetweenAnchorsOnScale({
                    value: barChartValue,
                    anchors: areaEmissionMethaneBins,
                    percentages: barGradientPercentages
                })
              : getLinearPositionOnScale({ min: barChartMin, max: barChartMax, value: barChartValue });

    const gradientString =
        product === AreaEmissionsProducts.l3
            ? buildLinearGradient(barGradientColors)
            : barGradientPercentages
              ? buildNonLinearGradient(barGradientColors, barGradientPercentages as number[])
              : buildLinearGradient(barGradientColors);

    const BarGradientPropValues: BarGradientProps = {
        gradientString,
        min: barChartMin,
        max: barChartMax,
        value: barChartValue,
        valuePosition: barGradientValuePosition,
        showValueLabel: false,
        // either positioned labels or min/max labels
        ...(positionedLabels ? { positionedLabels } : { minLabel: barChartMinLabel, maxLabel: barChartMaxLabel })
    };

    return {
        id,
        title,
        emissionsHeadline,
        dataRows,
        additionalContent,
        iconColor,
        platform,
        BarGradientPropValues,
        histogramContent,
        ogiNearbyContent
    };
};

/** Creates props for total-only accordions (used for captures with no dispersed sources) from a pick info object */
export const useGetTotalOnlyAccordionProps = (info: TotalOnlyAccordionProps['info']): TotalOnlyAccordionContent => {
    const t = useTranslate();
    const theme = useTheme();

    const product = useSelector(selectMethaneProduct);
    const platform = useSelector(selectPlatform);
    const units = useAreaEmissionsUnitsLabel(product);

    const id = getUniqueAccordionId(info);
    const title = t(`emissionsMapPage.mapModals.emissions.areaTitle.${product}`) || '';

    const totalMethane = getStacCaptureEmissions(info.object.properties).totalKgHr;

    const emissionsText = useGetEmissionsText({ platform, totalOnly: true });

    const emissionsHeadline = (
        <EmissionsHeadline
            emissions={NaN}
            emissionsText={emissionsText}
            units={units}
            typographyVariant="h6"
            data-testid={`dispersed-accordion-headline-${id}`}
        />
    );

    // Data rows
    const dataRows = useMethaneRasterDetailRows(info);

    // Bar chart props
    const l4MSATColorScale = useSelector(selectL4MSATColorScale);
    const colorScaleName = getRasterColorMapName(platform, product, l4MSATColorScale);

    const {
        MIN: areaEmissionsMin,
        MAX: areaEmissionsMax,
        ANCHOR_PERCENTAGES: areaEmissionsPercentages,
        LABELS: positionedLabels
    } = getColorScaleDefinition(platform, product, MethaneLayerIds.areaEmissionRaster, colorScaleName);
    const [barChartMin, barChartMax] = [areaEmissionsMin, areaEmissionsMax];

    const barGradientColors = getColorRangeColors(theme, true, product, platform, colorScaleName as ScaleDataKeys);
    const barGradientPercentages = areaEmissionsPercentages;

    const iconColor = colorArrayToRgb(getFilledTargetColor(totalMethane, platform));

    const barChartMinLabel = t('emissionsMapPage.drawerWithCards.barGradientMinLabel', {
        value: MIN_SIGNIFICANT_RASTER_EMISSION[platform]
    });
    const barChartMaxLabel = t('emissionsMapPage.drawerWithCards.barGradientMaxLabel', {
        value: formatEmissions(barChartMax)
    });

    const gradientString = barGradientPercentages
        ? buildNonLinearGradient(barGradientColors, barGradientPercentages as number[])
        : buildLinearGradient(barGradientColors);

    const BarGradientPropValues: BarGradientProps = {
        gradientString,
        min: barChartMin,
        max: barChartMax,
        showValueLabel: false,
        // either positioned labels or min/max labels
        ...(positionedLabels ? { positionedLabels } : { minLabel: barChartMinLabel, maxLabel: barChartMaxLabel })
    };

    return {
        id,
        title,
        emissionsHeadline,
        dataRows,
        iconColor,
        platform,
        BarGradientPropValues
    };
};
